// "Report a glitch": the last few minutes of trains that jumped or vanished
// on screen (glitch.ts), watched each frame the trains move, and sent with
// the view, the selection and the reader's comment.

import type { Map as MapLibreMap } from 'maplibre-gl';
import { glitchWatcher } from '@tol/core/glitch';
import type { Drawn } from '@tol/core/glitch';
import type { TrainStore } from '@tol/core/types';
import { el, esc } from './dom.ts';
import { bindReportDialog } from './report-dialog.ts';
import type { ShellState } from './state.ts';

export interface GlitchReportOptions {
  readonly map: MapLibreMap;
  readonly live: TrainStore;
  // The selection, Follow and the hidden lines, sent with a report.
  readonly state: ShellState;
  readonly city: string;
  readonly version: string;
  // The city's own trains on screen (London's National Rail).
  readonly drawn: (() => readonly Drawn[]) | undefined;
  readonly showToast: (html: string) => void;
}

export interface GlitchReport {
  // After a draw that moved the trains.
  readonly watch: () => void;
}

export function createGlitchReport(options: GlitchReportOptions): GlitchReport {
  const { map, live, state } = options;
  const glitches = glitchWatcher();
  function watch(): void {
    const drawn: Drawn[] = [];
    for (const t of live.trains.values()) {
      const px = t.render.px;
      if (!px) continue;
      const p = t.pred;
      drawn.push({
        id: t.id,
        x: px.x,
        y: px.y,
        alpha: t.render.shown ?? 0,
        label: `${p.lineId} ${p.vid || p.key} to ${p.dest} ${String(t.motion.pos?.P)}>${String(t.motion.pos?.N)}`,
      });
    }
    for (const d of options.drawn?.() ?? []) drawn.push(d);
    const c = map.getCenter();
    const { clientWidth, clientHeight } = map.getContainer();
    glitches.frame(
      Date.now(),
      `${c.lng},${c.lat},${map.getZoom()},${clientWidth}x${clientHeight}`,
      drawn,
      clientWidth,
      clientHeight,
    );
  }

  async function reportGlitch(comment: string): Promise<boolean> {
    const c = map.getCenter();
    const b = map.getBounds();
    const { clientWidth, clientHeight } = map.getContainer();
    const body = JSON.stringify({
      city: options.city,
      version: options.version,
      at: Date.now(),
      view: {
        center: [c.lng, c.lat],
        zoom: map.getZoom(),
        bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
        size: [clientWidth, clientHeight],
      },
      agent: navigator.userAgent,
      comment: comment.trim().slice(0, 1000),
      selected: state.selected,
      follow: state.follow,
      hidden: [...state.hidden],
      glitches: glitches.recent(Date.now()).slice(-100),
    });
    try {
      const res = await fetch('report', { method: 'POST', headers: { 'content-type': 'application/json' }, body });
      const reply: unknown = res.ok ? await res.json() : null;
      const ref =
        typeof reply === 'object' &&
        reply !== null &&
        'ref' in reply &&
        typeof reply.ref === 'string' &&
        reply.ref.trim() !== ''
          ? reply.ref
          : null;
      options.showToast(
        ref === null ? 'Couldn’t send the report. Try again?' : `Thanks, sent. Reference ${esc(ref.slice(11, 19))}`,
      );
      return ref !== null;
    } catch {
      options.showToast('Couldn’t send the report. Try again?');
      return false;
    }
  }
  const reportDialog = el('#report-dialog');
  const reportComment = el('#report-comment');
  if (reportDialog instanceof HTMLDialogElement && reportComment instanceof HTMLTextAreaElement)
    bindReportDialog(el('#report'), reportDialog, reportComment, reportGlitch);

  return { watch };
}
